import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { TESTS } from "@/data/tests";

/**
 * PorDondeEmpiezo — la puerta al test, al final de la portada.
 *
 * SALE DE `Landing.jsx` EL 7 DE OCTUBRE DE 2026 y lo obligó el trinquete: la portada estrenó
 * el esquema de la ruta —curso → módulo → lección— y se plantó en las 545 líneas con el
 * límite en 526. El corte es por donde hay un tema, y éste lo era: **esto no describe la
 * Academia, pide algo**, que es lo que hace un bloque de llamada y no una sección de portada.
 *
 * Y QUEDA UNA COSA POR DECIDIR, apuntada aquí para que no se pierda: desde hoy hay **tres**
 * puertas al mismo test —ésta, el botón de la sección de las cinco competencias y el de al
 * lado del buscador de `/cursos`—. Tres botones al mismo sitio con tres nombres distintos se
 * leen como tres cosas distintas; ésta es la candidata a caer, porque es la única que está
 * donde nadie se ha hecho todavía la pregunta.
 */
// LO QUE EL TEST DEVUELVE DE VERDAD. Aquí había cuatro cuadros -«Filósofo», «Psicólogo»,
// «Escritor», «Amante de las Humanidades»- que prometían un tipo de persona. El test no da
// eso desde que el «perfil Alpha» se quitó de los resultados, el 3 de septiembre, por sonar
// a horóscopo: da un ORDEN DE CURSOS con su plan. Un escaparate que enseña algo que el
// producto ya no hace no se descubre en el escaparate, se descubre al terminar el test, que
// es el peor sitio. Así que van los cuatro capítulos que se ven de verdad, en su orden.
const LO_QUE_SACAS = [
  ["Los cursos ordenados", "cuál primero, y por qué ese"],
  ["Los porcentajes de afinidad", "cuánto encaja cada uno, uno por uno"],
  // Y AQUI HABIA UN CUARTO: «El plan de estudio — lo traza el Planificador Alpha». Se cayo
  // el 20 de octubre de 2026, y no por estilo: **no lo traza nadie**. `generateStudyPlan`
  // existe en `lib/api/test.js` y ningun componente lo importa; `POST /api/planner/generate`
  // y `GET /api/planner/plan/{id}` no se llaman desde ninguna pantalla. O sea que esto no
  // prometia un componente que funciona regular: prometia una salida que no se produce.
  //
  // Y SON DOS COSAS DISTINTAS CON EL MISMO NOMBRE, que es de donde venia la confusion. Lo
  // que el test hace de verdad es ORIENTAR -ordena los cursos y da la afinidad-, y la
  // planificacion de las lecciones la hace Gabri como profesor; eso vale, lo que no vale es
  // atribuirselo a un automatismo. El otro Planificador es `AlphaPlanner`, la agenda de
  // examen de `AlphaLab`, y mientras el no este a la altura no se nombra aqui.
  //
  // Las tres que quedan se cumplen las tres: el orden y la afinidad los pinta
  // `pages/Resultados.jsx`, y el PDF sale de `getResultPdfUrl`.
  ["El documento oficial de la Academia", "en PDF, firmado y con su referencia"],
];

export default function PorDondeEmpiezo() {
  return (
    // El aire de arriba es la transición del negro de los testimonios a este blanco: el fondo
    // de la página cambia mientras cruza ese hueco, y por eso ahí no hay texto.
    <section className="pt-40 lg:pt-52 pb-24 lg:pb-32" data-tono="nacar">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="rounded-caja border border-alpha-black relative overflow-hidden p-10 md:p-16 lg:p-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="font-mono-alpha text-[11px] uppercase tracking-widest text-alpha-red mb-4">
                Empieza por algún sitio
              </div>
              <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                ¿Por dónde <span className="italic">empiezo</span>?
              </h2>
              {/* LA MISMA FRASE QUE LA FICHA DEL TEST, Y NO UNA COPIA. Aquí ponía
                  «Veintidós preguntas» tres semanas después de que el test se quedara en
                  cinco: una copia no da error el día que el test cambia, da una portada
                  que promete otra cosa. `tools/prueba_tests.py` no deja escribir aquí a
                  mano cuántas preguntas tiene nada. */}
              <p className="mt-6 text-alpha-black/70 text-lg max-w-lg text-justify">
                {TESTS.ruta.entradilla}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                {/* NEGRO Y CON LA ESTRELLITA, y el boton viene de `/precios`. Gabri, 20 de
                    octubre de 2026: *«lo que si me gusta mucho es el boton; pon este boton en
                    negro con esa estrellita en la landing, como sustituto del boton rojo»*.
                    Alli esa seccion se retira el mismo dia -era el mismo test de ruta contado
                    otra vez-, asi que lo que se queda de ella es lo unico que aportaba.

                    Y ADEMAS CUADRA CON LA DECISION DEL 19 DE OCTUBRE: el rojo de la casa es
                    UN acento -el punto, el rotulo, el ultimo escalon de la ruta-, no el color
                    de los botones. Este era el ultimo boton rojo grande de la portada. */}
                <Link
                  to="/test"
                  data-testid="cta-final-test"
                  className="rounded-full px-8 py-4 bg-alpha-black text-white font-mono-alpha uppercase text-[12px] tracking-widest btn-brutal inline-flex items-center gap-3"
                >
                  Hacer el test <Sparkles className="w-4 h-4" />
                </Link>
                <span className="text-sm text-alpha-black/50">
                  {TESTS.ruta.minutos} min · Resultados inmediatos
                </span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-caja border border-alpha-black/10" data-testid="test-lo-que-sacas">
                {LO_QUE_SACAS.map(([que, como], i) => (
                  <motion.div
                    key={que}
                    whileHover={{ x: 4 }}
                    className="flex items-start gap-4 p-5 border-b border-alpha-black/10 last:border-b-0"
                  >
                    <div className="font-mono-alpha text-[10px] uppercase tracking-widest text-alpha-red pt-2">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    {/* EL PIE, ENTRE PARÉNTESIS. Suelto debajo del sustantivo se leía como una
                        oración truncada, y el paréntesis dice lo que es: una aclaración del
                        sintagma de arriba, no una frase nueva. */}
                    <div>
                      <div className="font-serif-display text-xl leading-tight">{que}</div>
                      <div className="text-sm text-alpha-black/50 mt-1">({como})</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
